import { useMemo, useCallback } from 'react'
import { CalendarProps } from 'react-date-range'
import SmallSelect from '../SmallSelect'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { addYears } from 'date-fns'
import moment from 'moment-timezone'
import { generateRange } from '../../lib/utils'
import { useDateRangeStyles } from './useDateRangeStyles'


export type MonthAndYearSelectorProps = CalendarProps & {
  focusedDate: Date,
  changeShownDate: (val: Date | number | string, mode?: "set" | "setYear" | "setMonth" | "monthOffset") => void,
  isSingleMonth?: boolean
  canChangeMonthYear?: boolean
}

export function MonthAndYearSelector({
  focusedDate,
  changeShownDate,
  showMonthArrow,
  minDate,
  maxDate,
  showMonthAndYearPickers,
  isSingleMonth = false,
  canChangeMonthYear = true,
}: MonthAndYearSelectorProps) {
  const classes = useDateRangeStyles({ isSingleMonth })

  const monthNames = useMemo(() =>
    [...generateRange(0, 11)].map(m => moment().month(m).format('MMMM')),
    []
  )

  const handleMouseUp = useCallback((e: React.MouseEvent) => e.stopPropagation(), [])
  const handleClickPrev = useCallback(() => changeShownDate(-1, 'monthOffset'), [changeShownDate])
  const handleClickNext = useCallback(() => changeShownDate(+1, 'monthOffset'), [changeShownDate])
  const handleChangeMonth = useCallback((e) => changeShownDate(e.target.value, 'setMonth'), [changeShownDate])
  const handleChangeYear = useCallback((e) => changeShownDate(e.target.value, 'setYear'), [changeShownDate])

  const upperYearLimit = (maxDate || addYears(new Date(), 20)).getFullYear()
  const lowerYearLimit = (minDate || addYears(new Date(), -100)).getFullYear()

  return (
    <div onMouseUp={handleMouseUp} className={classes.monthAndYearWrapper}>
      {canChangeMonthYear && showMonthArrow && (
        <IconButton onClick={handleClickPrev} size="small" className={classes.prevNextButton}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}

      {canChangeMonthYear && showMonthAndYearPickers ? (
        <span>
          <SmallSelect value={focusedDate.getMonth()} onChange={handleChangeMonth} aria-label="Month">
            {monthNames.map((monthName, i) => (
              <MenuItem key={i} value={i}>{monthName}</MenuItem>
            ))}
          </SmallSelect>
          <span className={classes.monthAndYearDivider} />
          <SmallSelect value={focusedDate.getFullYear()} onChange={handleChangeYear} aria-label="Year">
            {new Array(upperYearLimit - lowerYearLimit + 1)
              .fill(upperYearLimit)
              .map((val, i) => {
                const year = val - i
                return <MenuItem key={year} value={year}>{year}</MenuItem>
              })}
          </SmallSelect>
        </span>
      ) : (
        <span>{monthNames[focusedDate.getMonth()]} {focusedDate.getFullYear()}</span>
      )}

      {canChangeMonthYear && showMonthArrow && (
        <IconButton onClick={handleClickNext} size="small" className={classes.prevNextButton}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  )
}
